import { useT } from '@transifex/react';
import { Text } from '@vidahealth/vida-ui';
import { View } from 'react-native';

import { SelectableRow } from '@/modules/ui';

import type { Address } from '../types';
import { composeCityLine } from '../utils/composeCityLine';
import { diffWords } from '../utils/diffWords';

import { AddressSummary } from './AddressSummary';

export type AddressChoice = 'verified' | 'entered';

// One line of the suggested address: the tokens Lob changed render bold.
//
// Every segment is an explicit vida-ui Text node: size + color are set on each one
// and bold is toggled on the changed tokens, so nothing relies on font-size
// inheritance (react-native-web doesn't inherit it into a Text nested under
// vida-ui's <span>).
// testID on the wrapping View since vida-ui's web Text leaks unknown props onto the
// DOM node.
const DiffLine = ({ from, to, testID }: { from: string; to: string; testID: string }) => {
  // Both sides empty → nothing to show.
  if (!to && !from) return null;
  // The member typed this line but Lob dropped it (e.g. an unnecessary unit) — show
  // an em dash so the removal is visible on the Suggested card instead of the row
  // silently vanishing. (Mirrors VRN's ComparedAddressLine "missing" placeholder.)
  if (!to) {
    return (
      <View testID={testID}>
        <Text size='md' bold className='text-typography-900'>
          —
        </Text>
      </View>
    );
  }
  const { b } = diffWords(from, to);
  return (
    <View testID={testID}>
      <Text size='md' className='text-typography-900'>
        {b.map((segment, index) => (
          <Text
            key={`${index}:${segment.text}`}
            size='md'
            bold={segment.highlighted}
            className='text-typography-900'
          >
            {segment.text}
          </Text>
        ))}
      </Text>
    </View>
  );
};

const SuggestedAddress = ({
  user,
  verified,
  testID,
}: {
  user: Address;
  verified: Address;
  testID: string;
}) => (
  <View testID={testID} className='gap-1'>
    <DiffLine from={user.addressLine1} to={verified.addressLine1} testID={`${testID}-line1`} />
    <DiffLine
      from={user.addressLine2 ?? ''}
      to={verified.addressLine2 ?? ''}
      testID={`${testID}-line2`}
    />
    <DiffLine
      from={composeCityLine(user)}
      to={composeCityLine(verified)}
      testID={`${testID}-city-line`}
    />
  </View>
);

type Props = {
  user: Address;
  verified: Address;
  selected: AddressChoice;
  onSelect: (choice: AddressChoice) => void;
  testID: string;
};

/**
 * The suggested-vs-typed chooser for the confirm dialog: two selectable cards
 * (single-select). The suggested card is pre-selected and emphasizes the tokens
 * Lob changed; the typed card shows the member's input as-is.
 */
export function ConfirmAddressCards({ user, verified, selected, onSelect, testID }: Props) {
  const t = useT();
  return (
    <View className='gap-3'>
      <SelectableRow
        selectionRole='radio'
        isSelected={selected === 'verified'}
        onPress={() => onSelect('verified')}
        accessibilityLabel={t('Use the suggested address')}
        testID={`${testID}-suggested`}
      >
        <View className='flex-1 gap-1'>
          <Text size='sm' className='font-semibold text-typography-600'>
            {t('Suggested')}
          </Text>
          <SuggestedAddress
            user={user}
            verified={verified}
            testID={`${testID}-suggested-address`}
          />
        </View>
      </SelectableRow>

      <SelectableRow
        selectionRole='radio'
        isSelected={selected === 'entered'}
        onPress={() => onSelect('entered')}
        accessibilityLabel={t('Use the address you typed')}
        testID={`${testID}-entered`}
      >
        <View className='flex-1 gap-1'>
          <Text size='sm' className='font-semibold text-typography-600'>
            {t('What you typed')}
          </Text>
          <AddressSummary address={user} testID={`${testID}-entered-address`} />
        </View>
      </SelectableRow>
    </View>
  );
}
